import {
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    Box,
    Chip,
    FormControlLabel,
    MenuItem,
    Paper,
    Skeleton,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';

import Chart, {
    useChart,
} from 'src/components/chart';

import {
    useCurrentWorkspace,
} from 'src/context/reducers/app-settings';

import {
    brandedCampaignsVisibility,
} from 'src/utils/branded-campaigns';

import {
    formatCurrency,
} from 'src/utils/format-currency';

import {
    formatNumber,
} from 'src/utils/format-number';


type DisplayMode =
    | 'Value'
    | 'Percent';


interface Props {
    data: any;
    error: any;
    isFetching: boolean;
}


interface BarDefinition {
    name: string;
    conversionKey: string;
    revenueKey: string;
    color: string;
}


const COLORS = {
    organic: '#18A055',

    searchPaid:
        '#FFB700',

    shoppingPaid:
        '#FF8C00',

    pmax:
        '#FFE299',

    blendedConversions:
        '#0052B4',

    blendedCvr:
        '#5119B7',

    blendedCostPerConv:
        '#D42186',

    paidCostPerConv:
        '#DAA335',
};


const HIDDEN_LINES = [
    'Blended CVR',
    'Blended Cost per Conv.',
    'Paid Cost per Conv.',
];

const MIN_LABEL_SEGMENT_HEIGHT = 10;


export default function ConversionsDistributionChart({
    data,
    error,
    isFetching,
}: Props) {
    const workspace: any =
        useCurrentWorkspace();


    const chartRef =
        useRef<any>(null);


    const chartContainerRef =
        useRef<HTMLDivElement | null>(
            null
        );


    const [
        showLabels,
        setShowLabels,
    ] = useState(false);


    const [
        displayMode,
        setDisplayMode,
    ] =
        useState<DisplayMode>(
            'Value'
        );


    const [
        hiddenSeries,
        setHiddenSeries,
    ] = useState<Set<string>>(
        () =>
            new Set(
                HIDDEN_LINES
            )
    );


    // -------------------------------------------------------
    // Workspace
    // -------------------------------------------------------

    const isLead =
        (
            workspace?.template ??
            ''
        )
            .toLowerCase()
            .includes('lead');


    const isCustom =
        Boolean(
            workspace?.custom
        );


    const {
        showShopping,
        showPmax,
    } =
        brandedCampaignsVisibility(
            workspace
        );


    const currency =
        workspace?.currency ??
        'USD';


    const lang =
        localStorage.getItem(
            'i18nextLng'
        ) ?? 'en';


    // -------------------------------------------------------
    // Time series
    // -------------------------------------------------------

    const timeSeries =
        data?.time_series ??
        [];


    const labels =
        useMemo(
            () =>
                timeSeries.map(
                    (row: any) =>
                        row?.dimension ??
                        row?.date ??
                        ''
                ),
            [timeSeries]
        );


    // -------------------------------------------------------
    // Bars
    // -------------------------------------------------------

    const barDefinitions =
        useMemo<
            BarDefinition[]
        >(
            () => {
                if (isCustom) {
                    return [
                        {
                            name:
                                'Organic',

                            conversionKey:
                                'organic_conversions',

                            revenueKey:
                                'organic_revenue',

                            color:
                                COLORS.organic,
                        },

                        {
                            name:
                                'Paid',

                            conversionKey:
                                'paid_conversions',

                            revenueKey:
                                'paid_revenue',

                            color:
                                COLORS.searchPaid,
                        },
                    ];
                }


                const bars:
                    BarDefinition[] =
                    [
                        {
                            name:
                                'Organic',

                            conversionKey:
                                'organic_conversions',

                            revenueKey:
                                'organic_revenue',

                            color:
                                COLORS.organic,
                        },

                        {
                            name:
                                'Search Paid',

                            conversionKey:
                                'search_paid_conversions',

                            revenueKey:
                                'search_paid_revenue',

                            color:
                                COLORS.searchPaid,
                        },
                    ];


                if (showShopping) {
                    bars.push({
                        name:
                            'Shopping Paid',

                        conversionKey:
                            'shopping_paid_conversions',

                        revenueKey:
                            'shopping_paid_revenue',

                        color:
                            COLORS.shoppingPaid,
                    });
                }


                if (showPmax) {
                    bars.push({
                        name:
                            'PMax',

                        conversionKey:
                            'pmax_paid_conversions',

                        revenueKey:
                            'pmax_paid_revenue',

                        color:
                            COLORS.pmax,
                    });
                }


                return bars;
            },
            [
                isCustom,
                showShopping,
                showPmax,
            ]
        );


    const rawBarValues =
        useMemo(
            () =>
                barDefinitions.map(
                    (
                        definition
                    ) => ({
                        name:
                            definition.name,

                        color:
                            definition.color,

                        values:
                            timeSeries.map(
                                (
                                    row: any
                                ) => {
                                    const key =
                                        isLead
                                            ? definition
                                                .conversionKey
                                            : definition
                                                .revenueKey;


                                    return (
                                        Number(
                                            row?.[
                                            key
                                            ]
                                        ) || 0
                                    );
                                }
                            ),
                    })
                ),
            [
                barDefinitions,
                timeSeries,
                isLead,
            ]
        );


    // -------------------------------------------------------
    // Value / Percent
    // -------------------------------------------------------

    const barSeries =
        useMemo(
            () => {
                if (
                    displayMode ===
                    'Value'
                ) {
                    return rawBarValues.map(
                        (
                            serie
                        ) => ({
                            name:
                                serie.name,

                            type:
                                'column',

                            data:
                                serie.values,
                        })
                    );
                }


                const totals =
                    timeSeries.map(
                        (
                            _: any,
                            index: number
                        ) =>
                            rawBarValues.reduce(
                                (
                                    total,
                                    serie
                                ) =>
                                    total +
                                    (
                                        serie
                                            .values[
                                        index
                                        ] ??
                                        0
                                    ),
                                0
                            )
                    );


                return rawBarValues.map(
                    (
                        serie
                    ) => ({
                        name:
                            serie.name,

                        type:
                            'column',

                        data:
                            serie.values.map(
                                (
                                    value: any,
                                    index: any
                                ) => {
                                    const total =
                                        totals[
                                        index
                                        ];


                                    if (!total) {
                                        return 0;
                                    }


                                    return (
                                        value /
                                        total
                                    ) * 100;
                                }
                            ),
                    })
                );
            },
            [
                displayMode,
                rawBarValues,
                timeSeries,
            ]
        );


    // -------------------------------------------------------
    // Lines
    // -------------------------------------------------------

    const lineSeries =
        useMemo(
            () => [
                {
                    name:
                        'Blended CVR',

                    type:
                        'line',

                    data:
                        timeSeries.map(
                            (
                                row: any
                            ) =>
                                Number(
                                    row
                                        ?.blended_cvr
                                ) || 0
                        ),
                },

                {
                    name:
                        'Blended Cost per Conv.',

                    type:
                        'line',

                    data:
                        timeSeries.map(
                            (
                                row: any
                            ) =>
                                Number(
                                    row
                                        ?.blended_cost_per_conv
                                ) || 0
                        ),
                },

                {
                    name:
                        'Paid Cost per Conv.',

                    type:
                        'line',

                    data:
                        timeSeries.map(
                            (
                                row: any
                            ) =>
                                Number(
                                    row
                                        ?.paid_cost_per_conv
                                ) || 0
                        ),
                },
            ],
            [timeSeries]
        );


    const series =
        useMemo(
            () => [
                ...barSeries,
                ...lineSeries,
            ],
            [
                barSeries,
                lineSeries,
            ]
        );


    // -------------------------------------------------------
    // Colors
    // -------------------------------------------------------

    const colors =
        useMemo(
            () => [
                ...barDefinitions.map(
                    (
                        bar
                    ) =>
                        bar.color
                ),

                COLORS.blendedCvr,

                COLORS.blendedCostPerConv,

                COLORS.paidCostPerConv,
            ],
            [barDefinitions]
        );


    // -------------------------------------------------------
    // Formatting
    // -------------------------------------------------------

    const formatBarValue = (
        value: number
    ) => {
        if (
            displayMode ===
            'Percent'
        ) {
            return `${Number(
                value ?? 0
            ).toFixed(1)}%`;
        }


        if (!isLead) {
            return formatCurrency(
                value,
                currency,
                lang,
                true,
                1
            );
        }


        return formatNumber(
            value
        );
    };


    const barCount =
        barSeries.length;


    // -------------------------------------------------------
    // Custom labels
    //
    // Même principe que ChartColumnStacked :
    //
    // [● 123K]
    // -------------------------------------------------------

    const labelAnnotations =
        showLabels
            ? barSeries.flatMap(
                (
                    serie: any,
                    seriesIndex: number
                ) => {
                    if (
                        hiddenSeries.has(
                            serie.name
                        )
                    ) {
                        return [];
                    }


                    return serie.data
                        .map(
                            (
                                value: number,
                                dataPointIndex: number
                            ) => {
                                const numericValue =
                                    Number(
                                        value
                                    );


                                if (
                                    !Number.isFinite(
                                        numericValue
                                    ) ||
                                    numericValue <=
                                    0
                                ) {
                                    return null;
                                }


                                let cumulativeValue =
                                    0;


                                for (
                                    let i = 0;
                                    i <=
                                    seriesIndex;
                                    i += 1
                                ) {
                                    const currentSeries =
                                        barSeries[
                                        i
                                        ];


                                    if (
                                        hiddenSeries.has(
                                            currentSeries
                                                .name
                                        )
                                    ) {
                                        continue;
                                    }


                                    const currentValue =
                                        Number(
                                            currentSeries
                                                .data[
                                            dataPointIndex
                                            ]
                                        );


                                    if (
                                        Number.isFinite(
                                            currentValue
                                        ) &&
                                        currentValue >
                                        0
                                    ) {
                                        cumulativeValue +=
                                            currentValue;
                                    }
                                }


                                return {
                                    x:
                                        labels[
                                        dataPointIndex
                                        ],

                                    y:
                                        cumulativeValue,

                                    marker: {
                                        size: 0,
                                        strokeWidth: 0,
                                        strokeColor: 'transparent',
                                        fillColor: 'transparent',
                                    },

                                    label: {
                                        text:
                                            formatBarValue(
                                                numericValue
                                            ),

                                        offsetY: 7,

                                        borderColor:
                                            'rgba(145, 158, 171, 0.20)',

                                        borderWidth: 1,

                                        borderRadius: 4,

                                        style: {
                                            background:
                                                '#FFFFFF',

                                            color:
                                                '#1C252E',

                                            fontSize:
                                                '10px',

                                            fontWeight:
                                                500,

                                            fontFamily:
                                                'Public Sans, sans-serif',

                                            cssClass:
                                                `chart-data-label chart-data-label-series-${seriesIndex} chart-data-label-point-${dataPointIndex}`,
                                        },
                                    },
                                };
                            }
                        )
                        .filter(
                            Boolean
                        );
                }
            )
            : [];


    const lineLabelAnnotations =
        showLabels
            ? lineSeries.flatMap(
                (serie: any, lineIndex: number) => {
                    if (hiddenSeries.has(serie.name)) {
                        return [];
                    }

                    const seriesIndex =
                        barSeries.length + lineIndex;

                    const seriesColor =
                        colors[seriesIndex] ?? '#637381';

                    return serie.data
                        .map(
                            (
                                value: number,
                                dataPointIndex: number
                            ) => {
                                const numericValue =
                                    Number(value);

                                if (
                                    !Number.isFinite(numericValue)
                                ) {
                                    return null;
                                }

                                let text = '';

                                if (
                                    serie.name ===
                                    'Blended CVR'
                                ) {
                                    text =
                                        `${numericValue.toFixed(1)}%`;
                                } else {
                                    text =
                                        formatCurrency(
                                            numericValue,
                                            currency,
                                            lang,
                                            true,
                                            1
                                        );
                                }

                                return {
                                    x: labels[dataPointIndex],

                                    // Contrairement aux barres :
                                    // position réelle de la ligne
                                    y: numericValue,

                                    marker: {
                                        size: 0,
                                        fillColor: 'transparent',
                                        strokeColor: 'transparent',
                                    },

                                    label: {
                                        text,

                                        // Place le label légèrement
                                        // au-dessus de la ligne
                                        offsetY: -8,

                                        borderColor:
                                            'rgba(145, 158, 171, 0.20)',

                                        borderWidth: 1,

                                        borderRadius: 4,

                                        style: {
                                            background: '#FFFFFF',
                                            color: '#1C252E',
                                            fontSize: '10px',
                                            fontWeight: 500,
                                            fontFamily:
                                                'Public Sans, sans-serif',

                                            cssClass:
                                                `chart-line-label chart-line-label-series-${lineIndex}`,
                                        },
                                    },
                                };
                            }
                        )
                        .filter(Boolean);
                }
            )
            : [];
    // -------------------------------------------------------
    // Inject colored dot in label
    // -------------------------------------------------------

    const injectSeriesDotsIntoLabels =
        () => {
            requestAnimationFrame(
                () => {
                    const container =
                        chartContainerRef
                            .current;


                    if (!container) {
                        return;
                    }


                    container
                        .querySelectorAll(
                            '.chart-label-dot'
                        )
                        .forEach(
                            (
                                dot
                            ) =>
                                dot.remove()
                        );


                    barSeries.forEach(
                        (
                            serie: any,
                            seriesIndex: number
                        ) => {
                            if (
                                hiddenSeries.has(
                                    serie.name
                                )
                            ) {
                                return;
                            }


                            const seriesColor =
                                colors[
                                seriesIndex
                                ] ??
                                '#637381';


                            const textElements =
                                container.querySelectorAll(
                                    `.chart-data-label-series-${seriesIndex} text, text.chart-data-label-series-${seriesIndex}`
                                );

                            textElements.forEach(
                                (
                                    node
                                ) => {
                                    const text =
                                        node as SVGTextElement;


                                    let rect =
                                        text.previousElementSibling as SVGRectElement | null;


                                    if (
                                        !rect ||
                                        rect.tagName.toLowerCase() !==
                                        'rect'
                                    ) {
                                        const parent =
                                            text.parentElement;


                                        if (
                                            !parent
                                        ) {
                                            return;
                                        }


                                        const siblings =
                                            Array.from(
                                                parent.children
                                            );


                                        const textIndex =
                                            siblings.indexOf(
                                                text
                                            );


                                        for (
                                            let i =
                                                textIndex -
                                                1;
                                            i >= 0;
                                            i -= 1
                                        ) {
                                            if (
                                                siblings[
                                                    i
                                                ].tagName.toLowerCase() ===
                                                'rect'
                                            ) {
                                                rect =
                                                    siblings[
                                                    i
                                                    ] as SVGRectElement;

                                                break;
                                            }
                                        }
                                    }


                                    if (!rect) {
                                        return;
                                    }


                                    const pointClass =
                                        Array.from(
                                            text.classList
                                        ).find(
                                            (className) =>
                                                className.startsWith(
                                                    'chart-data-label-point-'
                                                )
                                        );


                                    const dataPointIndex =
                                        pointClass
                                            ? Number(
                                                pointClass.replace(
                                                    'chart-data-label-point-',
                                                    ''
                                                )
                                            )
                                            : null;


                                    if (
                                        dataPointIndex !== null &&
                                        Number.isFinite(
                                            dataPointIndex
                                        )
                                    ) {
                                        const bar =
                                            container.querySelector<SVGPathElement>(
                                                `.apexcharts-bar-area[index="${seriesIndex}"][j="${dataPointIndex}"]`
                                            ) ??
                                            container
                                                .querySelectorAll<SVGElement>(
                                                    '.apexcharts-bar-series .apexcharts-series'
                                                )[seriesIndex]
                                                ?.querySelectorAll<SVGPathElement>(
                                                    '.apexcharts-bar-area'
                                                )[dataPointIndex] ??
                                            null;


                                        if (bar) {
                                            const barHeightAttribute =
                                                bar.getAttribute(
                                                    'barHeight'
                                                );


                                            let segmentHeight =
                                                barHeightAttribute !== null
                                                    ? Number(
                                                        barHeightAttribute
                                                    )
                                                    : Number.NaN;


                                            if (
                                                !Number.isFinite(
                                                    segmentHeight
                                                )
                                            ) {
                                                try {
                                                    segmentHeight =
                                                        bar.getBBox()
                                                            .height;
                                                } catch {
                                                    segmentHeight =
                                                        Number.POSITIVE_INFINITY;
                                                }
                                            }


                                            const shouldHide =
                                                Number.isFinite(
                                                    segmentHeight
                                                ) &&
                                                Math.abs(
                                                    segmentHeight
                                                ) <
                                                MIN_LABEL_SEGMENT_HEIGHT;


                                            if (shouldHide) {
                                                text.setAttribute(
                                                    'display',
                                                    'none'
                                                );

                                                rect.setAttribute(
                                                    'display',
                                                    'none'
                                                );

                                                return;
                                            }
                                        }
                                    }


                                    text.removeAttribute(
                                        'display'
                                    );

                                    rect.removeAttribute(
                                        'display'
                                    );


                                    const PADDING_X =
                                        8;

                                    const PADDING_Y =
                                        4;

                                    const GAP =
                                        4;

                                    const DOT_RADIUS =
                                        3;

                                    const DOT_SIZE =
                                        DOT_RADIUS *
                                        2;


                                    let textBBox:
                                        | DOMRect
                                        | SVGRect;


                                    try {
                                        textBBox =
                                            text.getBBox();
                                    } catch {
                                        return;
                                    }


                                    const textWidth =
                                        textBBox.width;

                                    const textHeight =
                                        textBBox.height;


                                    const chipWidth =
                                        PADDING_X +
                                        DOT_SIZE +
                                        GAP +
                                        textWidth +
                                        PADDING_X;


                                    const chipHeight =
                                        PADDING_Y +
                                        textHeight +
                                        PADDING_Y;


                                    const originalX =
                                        Number(
                                            rect.getAttribute(
                                                'x'
                                            ) || 0
                                        );


                                    const originalWidth =
                                        Number(
                                            rect.getAttribute(
                                                'width'
                                            ) || 0
                                        );


                                    const originalY =
                                        Number(
                                            rect.getAttribute(
                                                'y'
                                            ) || 0
                                        );


                                    const originalHeight =
                                        Number(
                                            rect.getAttribute(
                                                'height'
                                            ) || 0
                                        );


                                    const centerX =
                                        originalX +
                                        originalWidth /
                                        2;


                                    const centerY =
                                        originalY +
                                        originalHeight /
                                        2;


                                    const chipX =
                                        centerX -
                                        chipWidth /
                                        2;


                                    const chipY =
                                        centerY -
                                        chipHeight /
                                        2;


                                    rect.setAttribute(
                                        'x',
                                        String(
                                            chipX
                                        )
                                    );

                                    rect.setAttribute(
                                        'y',
                                        String(
                                            chipY
                                        )
                                    );

                                    rect.setAttribute(
                                        'width',
                                        String(
                                            chipWidth
                                        )
                                    );

                                    rect.setAttribute(
                                        'height',
                                        String(
                                            chipHeight
                                        )
                                    );

                                    rect.setAttribute(
                                        'rx',
                                        '4'
                                    );

                                    rect.setAttribute(
                                        'ry',
                                        '4'
                                    );


                                    const dotCenterX =
                                        chipX +
                                        PADDING_X +
                                        DOT_RADIUS;


                                    const dotCenterY =
                                        centerY;


                                    const textStartX =
                                        chipX +
                                        PADDING_X +
                                        DOT_SIZE +
                                        GAP;


                                    text.setAttribute(
                                        'text-anchor',
                                        'start'
                                    );


                                    text.setAttribute(
                                        'x',
                                        String(
                                            textStartX
                                        )
                                    );


                                    text.setAttribute(
                                        'y',
                                        String(
                                            centerY
                                        )
                                    );


                                    text.setAttribute(
                                        'dominant-baseline',
                                        'middle'
                                    );


                                    const circle =
                                        document.createElementNS(
                                            'http://www.w3.org/2000/svg',
                                            'circle'
                                        );


                                    circle.setAttribute(
                                        'class',
                                        'chart-label-dot'
                                    );


                                    circle.setAttribute(
                                        'cx',
                                        String(
                                            dotCenterX
                                        )
                                    );


                                    circle.setAttribute(
                                        'cy',
                                        String(
                                            dotCenterY
                                        )
                                    );


                                    circle.setAttribute(
                                        'r',
                                        String(
                                            DOT_RADIUS
                                        )
                                    );


                                    circle.setAttribute(
                                        'fill',
                                        seriesColor
                                    );


                                    circle.setAttribute(
                                        'stroke',
                                        'none'
                                    );


                                    text.parentNode?.insertBefore(
                                        circle,
                                        text
                                    );
                                }
                            );
                        }
                    );


                    lineSeries.forEach(
                        (
                            serie: any,
                            lineIndex: number
                        ) => {
                            if (
                                hiddenSeries.has(
                                    serie.name
                                )
                            ) {
                                return;
                            }

                            const seriesIndex =
                                barSeries.length +
                                lineIndex;

                            const seriesColor =
                                colors[
                                seriesIndex
                                ] ??
                                '#637381';

                            const textElements =
                                container.querySelectorAll(
                                    `.chart-line-label-series-${lineIndex} text, text.chart-line-label-series-${lineIndex}`
                                );

                            textElements.forEach(
                                (
                                    node
                                ) => {
                                    const text =
                                        node as SVGTextElement;

                                    let rect =
                                        text.previousElementSibling as SVGRectElement | null;

                                    if (
                                        !rect ||
                                        rect.tagName.toLowerCase() !==
                                        'rect'
                                    ) {
                                        const parent =
                                            text.parentElement;

                                        if (!parent) {
                                            return;
                                        }

                                        const siblings =
                                            Array.from(
                                                parent.children
                                            );

                                        const textIndex =
                                            siblings.indexOf(
                                                text
                                            );

                                        for (
                                            let i =
                                                textIndex - 1;
                                            i >= 0;
                                            i -= 1
                                        ) {
                                            if (
                                                siblings[
                                                    i
                                                ].tagName.toLowerCase() ===
                                                'rect'
                                            ) {
                                                rect =
                                                    siblings[
                                                    i
                                                    ] as SVGRectElement;

                                                break;
                                            }
                                        }
                                    }

                                    if (!rect) {
                                        return;
                                    }

                                    const PADDING_X =
                                        8;

                                    const PADDING_Y =
                                        4;

                                    const GAP =
                                        4;

                                    const DOT_RADIUS =
                                        3;

                                    const DOT_SIZE =
                                        DOT_RADIUS * 2;

                                    let textBBox:
                                        | DOMRect
                                        | SVGRect;

                                    try {
                                        textBBox =
                                            text.getBBox();
                                    } catch {
                                        return;
                                    }

                                    const textWidth =
                                        textBBox.width;

                                    const textHeight =
                                        textBBox.height;

                                    const chipWidth =
                                        PADDING_X +
                                        DOT_SIZE +
                                        GAP +
                                        textWidth +
                                        PADDING_X;

                                    const chipHeight =
                                        PADDING_Y +
                                        textHeight +
                                        PADDING_Y;

                                    const originalX =
                                        Number(
                                            rect.getAttribute(
                                                'x'
                                            ) || 0
                                        );

                                    const originalWidth =
                                        Number(
                                            rect.getAttribute(
                                                'width'
                                            ) || 0
                                        );

                                    const originalY =
                                        Number(
                                            rect.getAttribute(
                                                'y'
                                            ) || 0
                                        );

                                    const originalHeight =
                                        Number(
                                            rect.getAttribute(
                                                'height'
                                            ) || 0
                                        );

                                    const centerX =
                                        originalX +
                                        originalWidth / 2;

                                    const centerY =
                                        originalY +
                                        originalHeight / 2;

                                    const chipX =
                                        centerX -
                                        chipWidth / 2;

                                    const chipY =
                                        centerY -
                                        chipHeight / 2;

                                    rect.setAttribute(
                                        'x',
                                        String(
                                            chipX
                                        )
                                    );

                                    rect.setAttribute(
                                        'y',
                                        String(
                                            chipY
                                        )
                                    );

                                    rect.setAttribute(
                                        'width',
                                        String(
                                            chipWidth
                                        )
                                    );

                                    rect.setAttribute(
                                        'height',
                                        String(
                                            chipHeight
                                        )
                                    );

                                    rect.setAttribute(
                                        'rx',
                                        '4'
                                    );

                                    rect.setAttribute(
                                        'ry',
                                        '4'
                                    );

                                    const dotCenterX =
                                        chipX +
                                        PADDING_X +
                                        DOT_RADIUS;

                                    const dotCenterY =
                                        centerY;

                                    const textStartX =
                                        chipX +
                                        PADDING_X +
                                        DOT_SIZE +
                                        GAP;

                                    text.setAttribute(
                                        'text-anchor',
                                        'start'
                                    );

                                    text.setAttribute(
                                        'x',
                                        String(
                                            textStartX
                                        )
                                    );

                                    text.setAttribute(
                                        'y',
                                        String(
                                            centerY
                                        )
                                    );

                                    text.setAttribute(
                                        'dominant-baseline',
                                        'middle'
                                    );

                                    const circle =
                                        document.createElementNS(
                                            'http://www.w3.org/2000/svg',
                                            'circle'
                                        );

                                    circle.setAttribute(
                                        'class',
                                        'chart-label-dot'
                                    );

                                    circle.setAttribute(
                                        'cx',
                                        String(
                                            dotCenterX
                                        )
                                    );

                                    circle.setAttribute(
                                        'cy',
                                        String(
                                            dotCenterY
                                        )
                                    );

                                    circle.setAttribute(
                                        'r',
                                        String(
                                            DOT_RADIUS
                                        )
                                    );

                                    circle.setAttribute(
                                        'fill',
                                        seriesColor
                                    );

                                    circle.setAttribute(
                                        'stroke',
                                        'none'
                                    );

                                    text.parentNode?.insertBefore(
                                        circle,
                                        text
                                    );
                                }
                            );
                        }
                    );
                }
            );
        };


    // -------------------------------------------------------
    // Chart options
    // -------------------------------------------------------

    const chartOptions =
        useChart({
            chart: {
                stacked: true,

                stackOnlyBar:
                    true,

                toolbar: {
                    show: false,
                },

                events: {
                    mounted: (
                        chartContext: any
                    ) => {
                        HIDDEN_LINES.forEach(
                            (
                                name
                            ) => {
                                chartContext
                                    ?.hideSeries?.(
                                        name
                                    );
                            }
                        );


                        injectSeriesDotsIntoLabels();
                    },


                    animationEnd: () => {
                        injectSeriesDotsIntoLabels();
                    },


                    updated: () => {
                        injectSeriesDotsIntoLabels();
                    },


                    legendClick: (
                        _: any,
                        seriesIndex: number,
                        config: any
                    ) => {
                        const seriesName =
                            config?.config
                                ?.series?.[
                                seriesIndex
                            ]?.name;


                        if (
                            !seriesName
                        ) {
                            return;
                        }


                        setHiddenSeries(
                            (
                                previous
                            ) => {
                                const next =
                                    new Set(
                                        previous
                                    );


                                if (
                                    next.has(
                                        seriesName
                                    )
                                ) {
                                    next.delete(
                                        seriesName
                                    );
                                } else {
                                    next.add(
                                        seriesName
                                    );
                                }


                                return next;
                            }
                        );
                    },
                },
            },


            labels,


            colors,


            fill: {
                colors,
            },


            // ---------------------------------------------------
            // Stroke
            // ---------------------------------------------------

            stroke: {
                width:
                    series.map(
                        (
                            serie: any
                        ) =>
                            serie.type ===
                                'line'
                                ? 2
                                : 0
                    ),

                curve:
                    'smooth',
            },


            // ---------------------------------------------------
            // Bars
            // ---------------------------------------------------

            plotOptions: {
                bar: {
                    columnWidth:
                        '70%',

                    borderRadius: 2,

                    distributed:
                        false,

                    borderRadiusApplication:
                        'end',

                    borderRadiusWhenStacked:
                        'last',
                },
            },


            // ---------------------------------------------------
            // X axis
            // ---------------------------------------------------

            xaxis: {
                categories:
                    labels,

                labels: {
                    offsetY: 4,
                },
            },


            // ---------------------------------------------------
            // Legend
            //
            // IMPORTANT :
            // pas de custom marker.
            // Apex garde son marker natif.
            // ---------------------------------------------------

            legend: {
                show: true,
                clusterGroupedSeries: false,

                position:
                    'top',

                horizontalAlign:
                    'right',

                offsetY: 0,

                itemMargin: {
                    horizontal: 8,
                    vertical: 6,
                },
                markers: {
                    size: 6
                },

                labels: {
                    colors:
                        '#637381',
                },
            },


            // ---------------------------------------------------
            // Labels
            // ---------------------------------------------------

            dataLabels: {
                enabled:
                    false,
            },


            annotations: {
                points: [
                    ...labelAnnotations,
                    ...lineLabelAnnotations,
                ],
            },


            // ---------------------------------------------------
            // Grid
            // ---------------------------------------------------

            grid: {
                padding: {
                    top: 20,
                    bottom: 8,
                },
            },


            // ---------------------------------------------------
            // Y Axis
            // ---------------------------------------------------

            yaxis: [
                // Main axis: stacked bars (Conversions / Revenue).
                {
                    seriesName: [
                        ...barSeries.map(
                            (serie: any) =>
                                serie.name
                        ),
                    ],

                    min: 0,

                    max:
                        displayMode ===
                            'Percent'
                            ? 100
                            : undefined,

                    title: {
                        text:
                            isLead
                                ? 'Blended Conversions'
                                : 'Blended Revenue',
                        style: {
                            color: '#637381',
                            fontSize: '10px',
                            fontWeight: 500,
                            fontFamily:
                                'Public Sans, sans-serif',
                        },
                    },

                    labels: {
                        formatter: (
                            value: number
                        ) =>
                            formatBarValue(
                                value
                            ),
                    },
                },

                // Right axis: percentage line.
                {
                    seriesName:
                        'Blended CVR',

                    opposite: true,
                    min: 0,

                    title: {
                        text:
                            'Blended CVR',
                        style: {
                            color: '#637381',
                            fontSize: '10px',
                            fontWeight: 500,
                            fontFamily:
                                'Public Sans, sans-serif',
                        },
                    },

                    labels: {
                        formatter: (
                            value: number
                        ) =>
                            `${Number(
                                value ?? 0
                            ).toFixed(1)}%`,
                    },
                },

                // Right axis: currency lines.
                {
                    seriesName: [
                        'Blended Cost per Conv.',
                        'Paid Cost per Conv.',
                    ],

                    opposite: true,
                    min: 0,

                    title: {
                        text:
                            'Blended Cost per Conv. / Paid Cost per Conv.',
                        style: {
                            color: '#637381',
                            fontSize: '10px',
                            fontWeight: 500,
                            fontFamily:
                                'Public Sans, sans-serif',
                        },
                    },

                    labels: {
                        formatter: (
                            value: number
                        ) =>
                            formatCurrency(
                                value,
                                currency,
                                lang,
                                true,
                                1
                            ),
                    },
                },
            ],


            // ---------------------------------------------------
            // Tooltip
            // ---------------------------------------------------

            tooltip: {
                shared: true,
                intersect: false,

                custom: function ({
                    series: tooltipSeries,
                    dataPointIndex,
                    w,
                }: any) {
                    if (
                        !tooltipSeries ||
                        !w?.globals?.seriesNames ||
                        !w?.globals?.colors
                    ) {
                        return '';
                    }

                    const date =
                        labels?.[dataPointIndex] ?? '';

                    const collapsed = [
                        ...(w.globals.collapsedSeriesIndices ?? []),
                        ...(w.globals.ancillaryCollapsedSeriesIndices ?? []),
                    ];

                    const chartRows = tooltipSeries
                        .map((serie: any, seriesIndex: number) => {
                            if (collapsed.includes(seriesIndex)) {
                                return '';
                            }

                            const name =
                                w.globals.seriesNames[seriesIndex] ?? '';

                            const value =
                                Number(
                                    serie?.[dataPointIndex] ?? 0
                                );

                            let formattedValue = '0';

                            // BAR SERIES
                            if (seriesIndex < barCount) {
                                formattedValue =
                                    formatBarValue(value);
                            }

                            // LINE SERIES
                            else if (
                                name === 'Blended CVR'
                            ) {
                                formattedValue =
                                    `${value.toFixed(1)}%`;
                            } else {
                                formattedValue =
                                    formatCurrency(
                                        value,
                                        currency,
                                        lang,
                                        true,
                                        1
                                    );
                            }

                            const color =
                                w.globals.colors[
                                seriesIndex
                                ] ?? '#919EAB';

                            return `
                    <div
                        style="
                            display:flex;
                            align-items:center;
                            width:100%;
                            box-sizing:border-box;
                            padding:4px;
                            gap:8px;
                        "
                    >
                        <span
                            style="
                                display:inline-block;
                                width:4px;
                                height:20px;
                                border-radius:12px;
                                background-color:${color};
                                flex-shrink:0;
                            "
                        ></span>

                        <div
                            style="
                                flex:1;
                                min-width:0;
                                display:flex;
                                justify-content:space-between;
                                align-items:center;
                                gap:16px;

                                font-family:Public Sans,sans-serif;
                                font-size:13px;
                                font-weight:500;
                                color:#333C47;
                                white-space:nowrap;
                            "
                        >
                            <span>
                                ${name}
                            </span>

                            <span
                                style="
                                    flex-shrink:0;
                                    text-align:right;
                                "
                            >
                                ${formattedValue}
                            </span>
                        </div>
                    </div>
                `;
                        });

                    const blendedConversionsValue =
                        Number(
                            timeSeries?.[dataPointIndex]
                                ?.blended_conversions
                        ) || 0;

                    const blendedConversionsTooltipRow = `
                    <div
                        style="
                            display:flex;
                            align-items:center;
                            width:100%;
                            box-sizing:border-box;
                            padding:4px;
                            gap:8px;
                        "
                    >
                        <span
                            style="
                                display:inline-block;
                                width:4px;
                                height:20px;
                                border-radius:12px;
                                background-color:${COLORS.blendedConversions};
                                flex-shrink:0;
                            "
                        ></span>

                        <div
                            style="
                                flex:1;
                                min-width:0;
                                display:flex;
                                justify-content:space-between;
                                align-items:center;
                                gap:16px;

                                font-family:Public Sans,sans-serif;
                                font-size:13px;
                                font-weight:500;
                                color:#333C47;
                                white-space:nowrap;
                            "
                        >
                            <span>
                                Blended Conversions
                            </span>

                            <span
                                style="
                                    flex-shrink:0;
                                    text-align:right;
                                "
                            >
                                ${formatNumber(
                                    blendedConversionsValue
                                )}
                            </span>
                        </div>
                    </div>
                `;

                    const rows = [
                        ...chartRows.slice(0, barCount),
                        blendedConversionsTooltipRow,
                        ...chartRows.slice(barCount),
                    ].join('');

                    return `
            <div
                style="
                    width:320px;
                    min-width:320px;
                    max-width:320px;
                    box-sizing:border-box;

                    background:#FCFCFD;

                    border:none;
                    border-radius:12px;

                    box-shadow:
                        0px 4px 8px
                        rgba(145,158,171,0.16);

                    overflow:hidden;
                "
            >
                <div
                    style="
                        padding:
                            12px
                            12px
                            4px
                            12px;

                        box-sizing:border-box;
                    "
                >
                    <p
                        style="
                            font-family:
                                Public Sans,
                                sans-serif;

                            color:#333C47;

                            font-size:14px;
                            font-weight:500;
                            line-height:14px;

                            white-space:nowrap;

                            margin:0;
                            margin-bottom:12px;
                        "
                    >
                        ${date}
                    </p>
                </div>

                <div
                    style="
                        padding:
                            0
                            12px
                            12px
                            12px;

                        display:flex;
                        flex-direction:column;

                        box-sizing:border-box;
                    "
                >
                    ${rows}
                </div>
            </div>
        `;
                },
            },
        });


    // -------------------------------------------------------
    // Render
    // -------------------------------------------------------

    return (
        <Paper
            sx={{
                mt: 2,
                height: 395,

                // IMPORTANT : aucun padding ici
                p: 0,

                display: 'flex',
                flexDirection: 'column',

                borderRadius: '16px',
                border: '1px solid #EAEAEA',
                backgroundColor: '#FFF',
                boxShadow:
                    '0px 12px 24px -4px rgba(145, 158, 171, 0.12)',

                boxSizing: 'border-box',
                overflow: 'hidden',
            }}
        >
            {isFetching || (!data && !error) ? (
                <Skeleton
                    variant="rectangular"
                    animation="wave"
                    sx={{
                        width: '100%',
                        height: '100%',
                        borderRadius: 0,
                        transform: 'none',
                    }}
                />
            ) : (
                <Box
                    sx={{
                        p: 3, // 24px seulement quand le contenu est affiché
                        width: '100%',
                        height: '100%',
                        boxSizing: 'border-box',

                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="flex-start"
                        justifyContent="space-between"
                    >
                        <Box>
                            <Typography
                                variant="overline"
                                sx={{
                                    color: '#919EAB',
                                    display: 'block',
                                    lineHeight: 1.4,
                                }}
                            >
                                {isLead
                                    ? 'CONVERSIONS'
                                    : 'REVENUE'}
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: 16,
                                    fontWeight: 700,
                                    color: '#1C252E',
                                }}
                            >
                                {isLead
                                    ? 'Blended Conversions Distribution'
                                    : 'Blended Revenue Distribution'}
                            </Typography>
                        </Box>

                        <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                        >
                            <FormControlLabel
                                sx={{
                                    m: 0,
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: 13,
                                        color: '#637381',
                                    },
                                }}
                                control={
                                    <Switch
                                        size="small"
                                        checked={showLabels}
                                        onChange={(event) =>
                                            setShowLabels(
                                                event.target.checked
                                            )
                                        }
                                    />
                                }
                                label="Show labels"
                            />

                            <TextField
                                select
                                size="small"
                                value={displayMode}
                                onChange={(event) =>
                                    setDisplayMode(
                                        event.target.value as DisplayMode
                                    )
                                }
                                sx={{
                                    width: 110,
                                }}
                                SelectProps={{
                                    renderValue: (value) => (
                                        <Chip
                                            size="small"
                                            label={String(value)}
                                            sx={{
                                                backgroundColor:
                                                    'rgba(145, 158, 171, 0.12)',
                                                color: '#333C47',
                                            }}
                                        />
                                    ),
                                }}
                            >
                                <MenuItem value="Value">
                                    Value
                                </MenuItem>

                                <MenuItem value="Percent">
                                    Percent
                                </MenuItem>
                            </TextField>
                        </Stack>
                    </Stack>

                    <Box
                        ref={chartContainerRef}
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            width: '100%',

                            '& .apexcharts-legend': {
                                maxWidth: '100%',
                                whiteSpace: 'normal !important',
                            },

                            '& .apexcharts-legend-series': {
                                margin: '4px 8px !important',
                            },

                            '& .chart-data-label': {
                                filter:
                                    'drop-shadow(0 1px 1px rgba(0, 0, 0, 0.04))',
                            },

                            '& .chart-line-label': {
                                filter:
                                    'drop-shadow(0 1px 1px rgba(0, 0, 0, 0.04))',
                            },
                        }}
                    >
                        <Chart
                            ref={chartRef}
                            dir="ltr"
                            type="line"
                            series={series as any}
                            options={chartOptions}
                            height="100%"
                        />
                    </Box>
                </Box>
            )}
        </Paper>
    );
}